import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Acción asincrónica para el login
export const loginUser = createAsyncThunk(
  'user/login',
  async ({ username, password, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/api/users/login',
        { username, password, token },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
     return {
        id: response.data.id,
        username: response.data.username,
        userType: response.data.userType,
        profileImage: response.data.profileImage,  // Solo los datos esenciales
      };  // Retornamos los datos de login
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

// Acción asincrónica para logout en todos los dispositivos
export const logoutAllDevices = createAsyncThunk(
  'user/logoutAllDevices',
  async (password, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/users/logout-all', { password });
      return response.data;  // Devuelve el mensaje de éxito
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cerrar sesión en todos los dispositivos');
    }
  }
);

// Acción asincrónica para obtener el perfil del usuario
export const getUserProfile = createAsyncThunk(
  'user/getUserProfile',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get('/api/users/islogged'); // Llamada al backend para obtener el perfil
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch user profile'
      );
    }
  }
);

// Acción asincrónica para actualizar el perfil del usuario
export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async ( formData , { rejectWithValue }) => {
    try {
      // Aquí enviarías el FormData al backend
      const response = await axios.put('/api/users/update-profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // Asegúrate de enviar el tipo correcto para FormData
        },
      });
      
      // Regresamos la respuesta que contiene los datos actualizados
      return response.data;  // Esto debe contener los datos actualizados del usuario
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user profile');
    }
  }
);
// Acción asincrónica para logout
export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      // Llamamos al endpoint del backend para hacer logout
      await axios.post('/api/users/logout');  // Aquí Passport manejará la eliminación de la sesión y las cookies

      return true;  // Si todo fue exitoso, regresamos `true` para indicar que el logout fue realizado correctamente
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

// Acción asincrónica para cambiar la contraseña
export const changeUserPassword = createAsyncThunk(
  'user/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.put('/api/users/change-password', { currentPassword, newPassword });
      return response.data.message;  // Devuelve el mensaje de éxito
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cambiar la contraseña');
    }
  }
);

// Estado inicial del slice
const initialState = {
  loading: false,
  error: null,
  userInfo: null,  // Guardamos la información del usuario (login)
};

// Reducer y extraReducers
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    
    logout: (state) => {
      state.userInfo = null;  
    },
  },
  extraReducers: (builder) => {
    builder
      // Caso del login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Caso de getUserProfile
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = { ...state.userInfo, ...action.payload };
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Caso de logoutUser (limpiar estado en Redux)
      .addCase(logoutUser.fulfilled, (state) => {
        state.userInfo = null;  // Limpiamos el estado del usuario en Redux cuando el logout es exitoso
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = { ...state.userInfo, ...action.payload };  // Actualizamos el perfil con los nuevos datos
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutAllDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutAllDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = null;  // Limpiamos el estado del usuario en Redux cuando la sesión se cierra en todos los dispositivos
        alert(action.payload.message);  // Muestra el mensaje de éxito
      })
      .addCase(logoutAllDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        alert(action.payload);  // Muestra el error si falla
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state, action) => {
        state.loading = false;
        alert(action.payload);  // Muestra el mensaje de éxito
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        alert(action.payload);  // Muestra el error si falla
      });
  },
});

export const { logout } = userSlice.actions;
export default userSlice.reducer;
